import { cva } from 'class-variance-authority';

import { FOCUS_RING } from './kit';

/**
 * The one button of the tool UI.
 *
 * - `primary`: the main action of a surface (ink fill; `text-surface` is the
 *   "on-ink" color: white on near-black in light, near-black on near-white in
 *   dark, so it reads right on both without a dark variant).
 * - `secondary`: outline, the default for everything else.
 * - `ghost`: toolbar-ish, no chrome until hover.
 * - `danger`: solid red, ONLY to confirm a destructive action (the confirm
 *   dialog's button).
 * - `dangerOutline`: to START a destructive action (e.g. "Despublicar").
 * - `confirm`: success green, ONLY for the floating editor tool bars (the
 *   "Listo / Aplicar" button of a tool). Do not use it on pages.
 * - `quiet` / `quietDanger`: muted icon actions inside dense rows (folder
 *   tree, lists), always with `size="iconXs"`. `quietDanger` is for delete.
 *
 * Sizes: `iconXs` (24px) is only for icon actions inside dense rows (folder
 * tree, lists); everywhere else use `iconSm` / `icon`. `iconLg` (48px) is only
 * for the floating "create" button of a phone screen.
 *
 * Brand is deliberately not a button color: it is for focus rings, links and
 * selection.
 */
export const buttonVariants = cva(
  'inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap rounded-control border border-transparent text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 ' +
    FOCUS_RING,
  {
    variants: {
      variant: {
        primary: 'bg-ink text-surface hover:bg-ink/90',
        secondary: 'border-line-strong bg-surface text-ink hover:bg-surface-hover',
        ghost: 'text-ink-2 hover:bg-surface-hover hover:text-ink',
        danger: 'bg-danger text-danger-fg hover:bg-danger-hover',
        dangerOutline: 'border-danger/30 bg-surface text-danger hover:bg-danger-subtle',
        confirm: 'bg-success text-success-fg hover:bg-success/90',
        quiet:
          'text-ink-3 hover:bg-surface-sunken hover:text-ink data-[state=open]:bg-surface-sunken data-[state=open]:text-ink',
        quietDanger: 'text-ink-3 hover:bg-danger-subtle/50 hover:text-danger',
      },
      size: {
        sm: 'h-8 px-3',
        md: 'h-9 px-3.5',
        lg: 'h-12 px-6 text-base',
        icon: 'size-9 px-0',
        iconSm: 'size-8 px-0',
        iconXs: 'size-6 px-0',
        iconLg: 'size-12 px-0 [&_svg]:size-5',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);
